import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, CalendarPlus, Upload, Download, Users } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Calendar } from '@/shared/components/ui/Calendar';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { getUserColor } from '@/core/lib/colors';
import { KpiCard } from '@/shared/components/charts';
import { DownloadTemplateMenu } from '@/shared/components/custom/download-template-menu/DownloadTemplateMenu';
import { formatDisplayDate } from '@/shared/utils/dateUtils';
import { useTimeOffTypes } from '../../hooks/useTimeOffTypes';
import { useAdminTimeOffSummary } from '../../hooks/useAdminTimeOffSummary';
import { useTeamMemberBalances } from '../../hooks/useTeamMemberBalances';
import { RegisterAbsenceDialog } from './RegisterAbsenceDialog';
import { ImportAbsencesDialog } from './ImportAbsencesDialog';
import { ExportAbsencesDialog } from './ExportAbsencesDialog';
import type { TimeOffDateRangeQueryParams } from '../../types';

type QuickRange = 'today' | 'week' | 'month';

/**
 * `YYYY-MM-DD` from a local `Date`'s own fields — never `.toISOString()`,
 * which converts to UTC first and silently shifts date-only values by a day
 * in any negative-offset timezone.
 */
function toLocalDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function rangeForPreset(preset: QuickRange): TimeOffDateRangeQueryParams {
  const now = new Date();
  if (preset === 'today') {
    const today = toLocalDateOnly(now);
    return { from: today, to: today };
  }
  if (preset === 'week') {
    const dayOfWeek = now.getDay();
    const diffToMonday = (dayOfWeek + 6) % 7;
    const monday = new Date(now);
    monday.setDate(now.getDate() - diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: toLocalDateOnly(monday), to: toLocalDateOnly(sunday) };
  }
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toLocalDateOnly(from), to: toLocalDateOnly(to) };
}

function NotCalculableCell() {
  const { t } = useTranslation('timeOff');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-xs text-amber-600 dark:text-amber-400">—</span>
      </TooltipTrigger>
      <TooltipContent>{t('team.balances.notCalculable')}</TooltipContent>
    </Tooltip>
  );
}

/**
 * "Saldos" sub-tab of "Equipo" — Owner/Admin only. Unified absences panel:
 * quick filters + KPIs + a per-member balance table, plus the
 * register/import/export actions.
 *
 * @remarks
 * The "Asignados"/"Disponibles"/"Progreso" columns and the third KPI can't
 * be computed for real — there is no admin endpoint exposing another
 * member's balance (`TimeOffBalanceDto` is `/me/balance`, self-only). They
 * render as "not calculable" via `NotCalculableCell` until backend exposes
 * one; see `useTeamMemberBalances` and `tasks/current-story.md`.
 */
export function TeamBalancesPanel() {
  const { t } = useTranslation('timeOff');
  const [range, setRange] = useState<TimeOffDateRangeQueryParams>(() => rangeForPreset('month'));
  const [activePreset, setActivePreset] = useState<QuickRange | null>('month');
  const [typeId, setTypeId] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [rangePopoverOpen, setRangePopoverOpen] = useState(false);
  const [pendingRangeStart, setPendingRangeStart] = useState<Date | undefined>();
  const [pendingRangeEnd, setPendingRangeEnd] = useState<Date | undefined>();

  const { data: types } = useTimeOffTypes();
  const { data: summary } = useAdminTimeOffSummary(range);
  // Defaults to the first type once loaded, without a setState-in-effect —
  // the user's explicit choice (`typeId`) always wins once they pick one.
  const effectiveTypeId = typeId || (types?.[0]?.id ?? '');
  const { rows, isLoading, isError } = useTeamMemberBalances(range, effectiveTypeId);

  function selectPreset(preset: QuickRange) {
    setActivePreset(preset);
    setRange(rangeForPreset(preset));
  }

  function openRangePopover(open: boolean) {
    if (open) {
      setPendingRangeStart(new Date(`${range.from}T00:00:00`));
      setPendingRangeEnd(new Date(`${range.to}T00:00:00`));
    }
    setRangePopoverOpen(open);
  }

  function selectRangeDate(date: Date | undefined) {
    if (!date) return;

    if (!pendingRangeStart || (pendingRangeStart && pendingRangeEnd)) {
      setPendingRangeStart(date);
      setPendingRangeEnd(undefined);
      return;
    }

    const [start, end] =
      date < pendingRangeStart ? [date, pendingRangeStart] : [pendingRangeStart, date];
    setPendingRangeStart(start);
    setPendingRangeEnd(end);
    setActivePreset(null);
    setRange({ from: toLocalDateOnly(start), to: toLocalDateOnly(end) });
    setRangePopoverOpen(false);
  }

  const selectedTypeName = (types ?? []).find((type) => type.id === effectiveTypeId)?.name ?? '';
  const scopeRangeLabel = `${formatDisplayDate(range.from)} – ${formatDisplayDate(range.to)}`;

  return (
    <div className="space-y-6" data-testid="time-off-team-balances-panel">
      <Toolbar>
        <Toolbar.Info
          title={t('team.balances.panelTitle')}
          description={t('team.balances.panelDescription')}
        />
        <Toolbar.Actions>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRegisterOpen(true)}
            data-testid="time-off-team-balances-register-btn"
          >
            <CalendarPlus className="size-4" />
            {t('team.balances.registerAbsence')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setImportOpen(true)}
            data-testid="time-off-team-balances-import-btn"
          >
            <Upload className="size-4" />
            {t('team.balances.import')}
          </Button>
          <DownloadTemplateMenu
            csvHref="/templates/timeoff-historical-import-template.csv"
            xlsxHref="/templates/timeoff-historical-import-template.xlsx"
            csvLabel={t('team.balances.importDialog.downloadTemplateCsv')}
            xlsxLabel={t('team.balances.importDialog.downloadTemplateXlsx')}
            triggerLabel={t('team.balances.downloadTemplate')}
            testIdPrefix="time-off-team-balances"
            triggerVariant="labeled"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExportOpen(true)}
            data-testid="time-off-team-balances-export-btn"
          >
            <Download className="size-4" />
            {t('team.balances.export')}
          </Button>
        </Toolbar.Actions>
      </Toolbar>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center rounded-lg bg-muted p-1 text-muted-foreground">
          {(['today', 'week', 'month'] as const).map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => selectPreset(preset)}
              className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                activePreset === preset && 'bg-background text-foreground shadow-sm',
              )}
              data-testid={`time-off-team-balances-preset-${preset}`}
            >
              {t(
                `team.balances.quickRange.${preset === 'week' ? 'thisWeek' : preset === 'month' ? 'thisMonth' : preset}`,
              )}
            </button>
          ))}
        </div>

        <Popover open={rangePopoverOpen} onOpenChange={openRangePopover}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="time-off-team-balances-range-trigger">
              <CalendarIcon className="size-4" />
              {scopeRangeLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <Calendar
              rangeStart={pendingRangeStart}
              rangeEnd={pendingRangeEnd}
              onSelect={selectRangeDate}
            />
          </PopoverContent>
        </Popover>

        <Select value={effectiveTypeId} onValueChange={setTypeId}>
          <SelectTrigger size="sm" data-testid="time-off-team-balances-type-filter">
            <SelectValue placeholder={t('team.balances.typeFilterLabel')} />
          </SelectTrigger>
          <SelectContent>
            {(types ?? []).map((type) => (
              <SelectItem key={type.id} value={type.id ?? ''}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="px-4 py-3">
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              {t('team.balances.kpis.requestsInRange')}
            </p>
            <KpiCard value={summary?.totalRequests ?? 0} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="px-4 py-3">
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              {t('team.balances.kpis.absenceDaysTaken')}
            </p>
            <KpiCard value={summary?.totalAbsenceDays ?? 0} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex h-full flex-col justify-center gap-2 px-4 py-3">
            <p className="text-xs font-medium text-muted-foreground">
              {t('team.balances.kpis.membersWithoutAvailableDays')}
            </p>
            <NotCalculableCell />
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold">
          {t('team.balances.tableTitle', { type: selectedTypeName })}
        </h3>

        {isLoading && (
          <div className="space-y-2" data-testid="time-off-team-balances-loading-skeleton">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {isError && (
          <p className="text-sm text-muted-foreground" data-testid="time-off-team-balances-error">
            {t('team.error')}
          </p>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <div data-testid="time-off-team-balances-empty" className="py-8 text-center">
            <Users className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('team.balances.table.empty')}</p>
          </div>
        )}

        {!isLoading && !isError && rows.length > 0 && (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                    {t('team.balances.table.columns.member')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                    {t('team.balances.table.columns.type')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                    {t('team.balances.table.columns.assigned')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                    {t('team.balances.table.columns.taken')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                    {t('team.balances.table.columns.available')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                    {t('team.balances.table.columns.progress')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.userId}
                    className="border-t"
                    data-testid={`time-off-team-balance-row-${row.userId}`}
                  >
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <UserAvatar
                          name={row.displayName}
                          color={getUserColor(row.userId)}
                          size="xs"
                        />
                        <span className="truncate">{row.displayName}</span>
                      </div>
                    </td>
                    <td className="p-2">{selectedTypeName}</td>
                    <td className="p-2">
                      <NotCalculableCell />
                    </td>
                    <td className="p-2">{row.takenDays}</td>
                    <td className="p-2">
                      <NotCalculableCell />
                    </td>
                    <td className="p-2">
                      <NotCalculableCell />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RegisterAbsenceDialog open={registerOpen} onOpenChange={setRegisterOpen} />
      <ImportAbsencesDialog open={importOpen} onOpenChange={setImportOpen} />
      <ExportAbsencesDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        scopeRangeLabel={scopeRangeLabel}
        scopeTypeLabel={selectedTypeName}
      />
    </div>
  );
}
